'use client';

import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Brand, Search } from '@zoomit/icons';
import { Box, Breakpoints, Col, Flex, Link } from '@zoomit/primitives';
import type {
  SearchSize} from '@zoomit/web-kit';
import {
  isMobile,
  ProductInfoSheet,
  ProductListItemMobile,
  ProductListItemMobileSkeleton,
  useProductInfoSheet,
} from '@zoomit/web-kit';
import { cssVar, Hr, List, ListItem, RedDot, TextIcon, theme, Typography } from '@zoomit/zoomit-ds';

import { SPECIFICATIONS_ID, STORES_ID } from '../../constants/single-product';
import type { ArticlesSearchResultType, ProductSearchResponse, SearchInstanceResult } from '../../types/search';
import ProductSimpleListItemDesktop from '../shared/ProductSimpleListItemDesktop';
import ProductSimpleListItemDesktopSkeleton from '../shared/ProductSimpleListItemDesktopSkeleton';
import KeyWordSectionSkeleton from './KeyWordSectionSkeleton';
import TextItemSkeleton from './TextItemSkeleton';

type Props = {
  variant?: SearchSize;
  searchQuery: string;
  products: ProductSearchResponse['products']['source'];
  articles: ArticlesSearchResultType['articles']['source'];
  categories: ProductSearchResponse['categories'];
  keywordCategories: ProductSearchResponse['keywordCategories'];
  isLoading: boolean;
  onClose: () => void;
} & SearchInstanceResult;

const SearchResult = (props: Props) => {
  const {
    icon,
    title,
    value,
    searchQuery,
    maxCount,
    products,
    categories,
    keywordCategories,
    articles,
    onClose,
    isLoading,
    variant = 'auto',
  } = props;
  if (!searchQuery) return;

  const TitleColSizeOnMd = variant === 'auto' ? 2 : 12;
  const ContentColSizeOnMd = variant === 'auto' ? 10 : 12;

  const ArticleSection = () => {
    const Wrapper = ({ children }: { children: ReactNode }) => (
      <>
        <ContentWrapper variant={variant}>
          <Col md={TitleColSizeOnMd} size={12}>
            <TitleWrapper>
              <Box spacing={{ mt: -0.2 }}>{icon(isMobile ? cssVar(theme.primary1) : cssVar(theme.text))}</Box>
              <Typography
                variant="subhead-bold"
                textColor={isMobile ? cssVar(theme.primary2) : cssVar(theme.text)}
              >
                {isMobile ? `${title} یافت شده با عبارت “ ${searchQuery} “` : title}
              </Typography>
            </TitleWrapper>
          </Col>
          <Col md={ContentColSizeOnMd} size={12}>
            <List>{children}</List>
          </Col>
        </ContentWrapper>
        {!isMobile && !isLoading && <Hr />}
      </>
    );

    if (isLoading)
      return (
        <Wrapper>
          <TextItemSkeleton countOfSkeletons={maxCount} />
        </Wrapper>
      );

    if (articles.length === 0) return;

    return (
      <Wrapper>
        {articles.map(({ title, slug, id }) => (
          <ListItem onClick={onClose} indicator={<RedDot />} key={id} spacing={{ mb: 'x2' }}>
            <OutSideLink href={`/${slug}`}>
              <Typography variant="callout" lineHeight={1.6}>
                {title}
              </Typography>
            </OutSideLink>
          </ListItem>
        ))}
      </Wrapper>
    );
  };

  const ProductSection = () => {
    const { handleSetProductInfoAndOpen, ...sheetOptions } = useProductInfoSheet();
    const Wrapper = ({ children }: { children: ReactNode }) => (
      <>
        <ContentWrapper variant={variant}>
          <Col md={TitleColSizeOnMd} size={12}>
            <TitleWrapper>
              <Box spacing={{ mt: -0.2 }}>{icon(isMobile ? cssVar(theme.primary1) : cssVar(theme.text))}</Box>
              <Typography
                variant="subhead-bold"
                textColor={isMobile ? cssVar(theme.primary2) : cssVar(theme.text)}
              >
                {isMobile ? `${title} یافت شده با عبارت “ ${searchQuery} “` : title}
              </Typography>
            </TitleWrapper>
          </Col>
          <Col md={ContentColSizeOnMd} size={12}>
            <Flex gap={0.8} flexDirection="column">
              {children}
            </Flex>
          </Col>
        </ContentWrapper>
        <Hr />
      </>
    );

    if (isLoading)
      return (
        <Wrapper>
          {Array.from({ length: maxCount }).map((_, index) =>
            isMobile ? (
              <ProductListItemMobileSkeleton key={index} />
            ) : (
              <ProductSimpleListItemDesktopSkeleton key={index} />
            ),
          )}
        </Wrapper>
      );

    if (products.length === 0) return;

    return (
      <Wrapper>
        {products.map((product) =>
          isMobile ? (
            <div key={product.id} onClick={onClose}>
              <ProductListItemMobile {...product} onMore={() => handleSetProductInfoAndOpen(product)} />
            </div>
          ) : (
            <div onClick={onClose} key={product.id}>
              <ProductSimpleListItemDesktop {...product} />
            </div>
          ),
        )}
        <ProductInfoSheet
          hasCompare={false}
          {...sheetOptions}
          specificationsId={SPECIFICATIONS_ID}
          storesId={STORES_ID}
        />
      </Wrapper>
    );
  };

  const KeyWordAndCategorySection = () => {
    const Wrapper = ({ children }: { children: ReactNode }) => (
      <Box>
        <Flex justifyContent="flex-end" spacing={{ py: 'x4', pr: 'x4' }}>
          <Col md={ContentColSizeOnMd} size={12}>
            <Flex flexDirection="column" gap={1.2}>
              {children}
            </Flex>
          </Col>
        </Flex>
        {!isMobile && <Hr />}
      </Box>
    );

    if (isLoading)
      return (
        <Wrapper>
          <KeyWordSectionSkeleton />
        </Wrapper>
      );

    if (keywordCategories.length === 0 && categories.length === 0) return;

    return (
      <Wrapper>
        {categories.map(({ id, slug, title }) => (
          <Link onClick={onClose} href={`/list/${slug}`} key={id}>
            <Flex gap={0.8} alignItems="center">
              <Brand fill={cssVar(theme.grey1)} />
              <Typography>همه محصولات {title}</Typography>
            </Flex>
          </Link>
        ))}
        {keywordCategories.map(({ id, slug, title }) => (
          <Link onClick={onClose} href={`/list/${slug}?query=${searchQuery}`} key={id}>
            <Flex gap={0.8} alignItems="center">
              <Search fill={cssVar(theme.grey1)} />
              <Typography>
                {searchQuery} در دسته {title}
              </Typography>
            </Flex>
          </Link>
        ))}
      </Wrapper>
    );
  };

  if (value === 'articles') return <ArticleSection />;

  if (value === 'products')
    return (
      <>
        <ProductSection />
        <KeyWordAndCategorySection />
      </>
    );
};

export default SearchResult;

const ContentWrapper = styled.div<{ variant: SearchSize }>`
  margin-block: 1.6rem;
  gap: 1.6rem;
  min-height: 3.2rem;
  display: flex;

  ${({ variant }) =>
    variant === 'small' &&
    css`
      flex-direction: column;
      padding-inline: 0.2rem;
    `};

  @media (max-width: ${Breakpoints.md}) {
    flex-direction: column;
    padding-inline: 0.2rem;
  }
`;

const TitleWrapper = styled(TextIcon)`
  margin-top: 0.5rem;
  align-items: inherit;
  justify-content: inherit;
`;

const OutSideLink = styled.a`
  text-decoration: none;
  &:hover * {
    color: var(${theme.primary2});
  }
`;
